import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppSettings } from '../../utils/app-settings';
import {
  ICoordListResponse,
  ITabCounts,
  IMedicationRequest,
  ICreateMedicationRequest,
  IUpdateMedicationRequest,
  IImportResult,
  IImportPreviewResult,
  IImportDecision,
  IPdfImportResult,
  IPdfStagingRow,
  IPdfStagingUploadResult,
  IPdfStagingPageResult,
  IPdfStagingBulkLockResult,
  IPdfStagingFilters,
  IPdfStagingTabCounts,
  IOcrStats,
  IStatesPiechart,
  IContactsLinechart,
  IContactStateCounts,
  CoordTab,
  CoordStatus,
  CoordPriority,
} from '../../interfaces/coordinacion.interface';
import { IApiResponse } from '../../interfaces/api-response.interface';

@Injectable({ providedIn: 'root' })
export class CoordinacionService {
  constructor(private http: HttpClient, private appSettings: AppSettings) {}

  getAll(params: {
    page?: number;
    limit?: number;
    tab?: CoordTab;
    search?: string;
    status?: CoordStatus | '';
    priority?: CoordPriority | '';
    dateFrom?: string;
    dateTo?: string;
  }): Observable<IApiResponse<ICoordListResponse>> {
    let httpParams = new HttpParams();
    if (params.page) httpParams = httpParams.set('page', params.page.toString());
    if (params.limit) httpParams = httpParams.set('limit', params.limit.toString());
    if (params.tab) httpParams = httpParams.set('tab', params.tab);
    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.priority) httpParams = httpParams.set('priority', params.priority);
    if (params.dateFrom) httpParams = httpParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) httpParams = httpParams.set('dateTo', params.dateTo);
    return this.http.get<IApiResponse<ICoordListResponse>>(
      this.appSettings.coordinacion.urls.getAll,
      { params: httpParams }
    );
  }

  getTabCounts(dateFrom?: string): Observable<IApiResponse<ITabCounts>> {
    return this.http.get<IApiResponse<ITabCounts>>(
      this.appSettings.coordinacion.urls.getTabCounts,
      ...(dateFrom ? [{ params: { dateFrom } }] as const : []),
    );
  }

  /** Contadores por estado puro del dashboard (Home AC). Ver IContactStateCounts. */
  getContactStateCounts(): Observable<IApiResponse<IContactStateCounts>> {
    return this.http.get<IApiResponse<IContactStateCounts>>(
      this.appSettings.coordinacion.urls.getContactStateCounts
    );
  }

  getOcrStats(): Observable<IApiResponse<IOcrStats>> {
    return this.http.get<IApiResponse<IOcrStats>>(
      this.appSettings.coordinacion.urls.getOcrStats
    );
  }

  getStatesPiechart(): Observable<IApiResponse<IStatesPiechart>> {
    return this.http.get<IApiResponse<IStatesPiechart>>(
      this.appSettings.coordinacion.urls.getStatesPiechart
    );
  }

  getContactsLinechart(startDate: string, endDate: string): Observable<IApiResponse<IContactsLinechart>> {
    const params = new HttpParams().set('startDate', startDate).set('endDate', endDate);
    return this.http.get<IApiResponse<IContactsLinechart>>(
      this.appSettings.coordinacion.urls.getContactsLinechart,
      { params }
    );
  }

  getById(id: string): Observable<IApiResponse<IMedicationRequest>> {
    return this.http.get<IApiResponse<IMedicationRequest>>(
      `${this.appSettings.coordinacion.urls.getAll}/${id}`
    );
  }

  create(body: ICreateMedicationRequest): Observable<IApiResponse<IMedicationRequest>> {
    return this.http.post<IApiResponse<IMedicationRequest>>(
      this.appSettings.coordinacion.urls.getAll,
      body
    );
  }

  update(id: string, body: IUpdateMedicationRequest): Observable<IApiResponse<IMedicationRequest>> {
    return this.http.patch<IApiResponse<IMedicationRequest>>(
      `${this.appSettings.coordinacion.urls.getAll}/${id}`,
      body
    );
  }

  importExcel(file: File): Observable<IApiResponse<IImportPreviewResult>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<IApiResponse<IImportPreviewResult>>(
      this.appSettings.coordinacion.urls.importExcel,
      formData
    );
  }

  // Paso 2 del import: confirma las decisiones del operador sobre los conflictos
  // detectados en el preview. Los conflictos no incluidos se omiten en el backend.
  resolveImport(decisions: IImportDecision[]): Observable<IApiResponse<IImportResult>> {
    return this.http.post<IApiResponse<IImportResult>>(
      this.appSettings.coordinacion.urls.importExcelConfirm,
      { decisions }
    );
  }

  importPdf(files: File[]): Observable<IApiResponse<IPdfImportResult>> {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return this.http.post<IApiResponse<IPdfImportResult>>(
      this.appSettings.coordinacion.urls.importPdf,
      formData
    );
  }

  // PDF Staging
  addPdfToStaging(files: File[]): Observable<IApiResponse<IPdfStagingUploadResult>> {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return this.http.post<IApiResponse<IPdfStagingUploadResult>>(
      this.appSettings.coordinacion.urls.pdfStaging,
      formData
    );
  }

  getStagingRows(filters?: IPdfStagingFilters): Observable<IApiResponse<IPdfStagingPageResult>> {
    let params = new HttpParams();
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.search) params = params.set('search', filters.search);
    if (filters?.province) params = params.set('province', filters.province);
    if (filters?.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params = params.set('dateTo', filters.dateTo);
    if (filters?.page) params = params.set('page', String(filters.page));
    if (filters?.limit) params = params.set('limit', String(filters.limit));
    return this.http.get<IApiResponse<IPdfStagingPageResult>>(
      this.appSettings.coordinacion.urls.pdfStaging,
      { params }
    );
  }

  /** Contadores por solapa del listado OCR. Respeta search/province/fechas, ignora la solapa. */
  getStagingTabCounts(filters?: IPdfStagingFilters): Observable<IApiResponse<IPdfStagingTabCounts>> {
    let params = new HttpParams();
    if (filters?.search) params = params.set('search', filters.search);
    if (filters?.province) params = params.set('province', filters.province);
    if (filters?.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params = params.set('dateTo', filters.dateTo);
    return this.http.get<IApiResponse<IPdfStagingTabCounts>>(
      `${this.appSettings.coordinacion.urls.pdfStaging}/stats/tabs`,
      { params }
    );
  }

  deleteStagingRow(id: string, expectedUpdatedAt?: string): Observable<void> {
    const options = expectedUpdatedAt
      ? { params: new HttpParams().set('expectedUpdatedAt', expectedUpdatedAt) }
      : {};
    return this.http.delete<void>(
      `${this.appSettings.coordinacion.urls.pdfStaging}/${id}`,
      options
    );
  }

  bulkLockStagingRows(ids: string[]): Observable<IApiResponse<IPdfStagingBulkLockResult>> {
    return this.http.post<IApiResponse<IPdfStagingBulkLockResult>>(
      this.appSettings.coordinacion.urls.pdfStagingBulkLock,
      { ids }
    );
  }

  flagStagingRows(ids: string[], flag: boolean): Observable<IApiResponse<{ flagged: string[]; skipped: string[] }>> {
    return this.http.post<IApiResponse<{ flagged: string[]; skipped: string[] }>>(
      this.appSettings.coordinacion.urls.pdfStagingBulkFlag,
      { ids, flag }
    );
  }

  lockStagingRow(id: string): Observable<IApiResponse<IPdfStagingRow>> {
    return this.http.patch<IApiResponse<IPdfStagingRow>>(
      this.appSettings.coordinacion.urls.pdfStagingLock(id),
      {}
    );
  }

  unlockStagingRow(id: string): Observable<IApiResponse<IPdfStagingRow>> {
    return this.http.patch<IApiResponse<IPdfStagingRow>>(
      this.appSettings.coordinacion.urls.pdfStagingUnlock(id),
      {}
    );
  }

  confirmStagingRow(id: string, data: Partial<IPdfStagingRow>): Observable<IApiResponse<IPdfStagingRow>> {
    return this.http.patch<IApiResponse<IPdfStagingRow>>(
      this.appSettings.coordinacion.urls.pdfStagingConfirm(id),
      data
    );
  }

  assignStagingClientId(id: string, clientId: string): Observable<IApiResponse<IPdfStagingRow>> {
    return this.http.patch<IApiResponse<IPdfStagingRow>>(
      this.appSettings.coordinacion.urls.pdfStagingAssignClient(id),
      { clientId }
    );
  }

  renewStagingLock(id: string): Observable<void> {
    return this.http.patch<void>(
      this.appSettings.coordinacion.urls.pdfStagingHeartbeat(id),
      {}
    );
  }

  exportProcessedRows(ids?: string[]): Observable<Blob> {
    return this.http.post(
      this.appSettings.coordinacion.urls.pdfStagingExportFiltered,
      { ids },
      { responseType: 'blob' }
    );
  }

  getStagingPdfUrl(id: string, token: string): string {
    return `${this.appSettings.coordinacion.urls.pdfStagingPdf(id)}`;
  }

  sendMessages(ids: string[], force = false): Observable<IApiResponse<any>> {
    return this.http.post<IApiResponse<any>>(
      this.appSettings.coordinacion.urls.sendMessages,
      { ids, force }
    );
  }

  bulkDelete(ids: string[]): Observable<IApiResponse<any>> {
    return this.http.delete<IApiResponse<any>>(
      this.appSettings.coordinacion.urls.bulkDelete,
      { body: { ids } }
    );
  }

  enableFromReview(id: string): Observable<IApiResponse<any>> {
    return this.http.post<IApiResponse<any>>(
      this.appSettings.coordinacion.urls.enableFromReview(id),
      {},
    );
  }
}
